import bootstrapped from '../Common/bootstrapped';
import { Texts as t } from '../Common/Texts';
import React, { useState, useEffect, ReactElement } from 'react';

type DatePickerProps = {
    selectableDates?: string[];
    selectedDate?: string;
    locale?: string;
    minAllowedDate?: string;
    maxAllowedDate?: string;
    onDateSelected?: (date: string) => void;
};

/**
 * The `DatePicker` component provides a date picker UI with various customization options.
 *
 * @param selectedDate - The currently selected date, in the format 'YYYY-MM-DD'.
 * @param selectableDates - An array of selectable dates in the format 'YYYY-MM-DD'.
 * @param locale - The locale to use for displaying month and day names.
 * @param minAllowedDate - The minimum allowed date, in the format 'YYYY-MM-DD'.
 * @param maxAllowedDate - The maximum allowed date, in the format 'YYYY-MM-DD'.
 * @param onDateSelected - A callback function that is called when a date is selected, with the selected date in the format 'YYYY-MM-DD'.
 */
export function DatePicker({
    selectedDate = null,
    selectableDates = [],
    locale = bootstrapped.CurrentLocale,
    minAllowedDate = null,
    maxAllowedDate = null,
    onDateSelected,
}: DatePickerProps) {
    const [firstDayOfWeek, setFirstDayOfWeek] = useState<number>(0);
    const [parsedSelectableDates, setParsedSelectableDates] = useState<
        string[]
    >([]);
    const [monthNames, setMonthNames] = useState<string[]>([]);
    const [dayNames, setDayNames] = useState<string[]>([]);
    const startDate = new Date();
    startDate.setDate(1);
    const [date, setDate] = useState<Date>(startDate);
    const year = date.getFullYear();
    const month = date.getMonth();
    const [currentDate, setCurrentDate] = useState<string | null>(selectedDate);

    useEffect(() => {
        setFirstDayOfWeek(getFirstDayOfWeek(locale));
    }, [locale]);

    useEffect(() => {
        setMonthNames(getLocaleMonthNames(locale));
        setDayNames(getLocaleDayNames(locale, firstDayOfWeek));
    }, [firstDayOfWeek]);

    useEffect(() => {
        setParsedSelectableDates(selectableDates);
        setCurrentDate(selectedDate);
    }, [selectableDates, selectedDate]);

    const getFirstDayOfWeek = (locale: string) => {
        if (typeof Intl.Locale.prototype.getWeekInfo === 'function') {
            // @ts-expect-error - Intl.Locale.prototype.getWeekInfo is not typed, but it works.
            const firstDay = new Intl.Locale(locale).getWeekInfo().firstDay;
            return firstDay === 7 ? 0 : firstDay;
        }
        return 1; // default to Monday
    };

    const getLocaleMonthNames = (locale: string) => {
        const formatter = new Intl.DateTimeFormat(locale, {
            month: 'long',
        });
        const months = [];
        for (let month = 0; month < 12; month++) {
            const date = new Date(Date.UTC(2021, month, 1));
            months.push(formatter.format(date));
        }
        return months;
    };

    const getLocaleDayNames = (locale: string, firstDayOfWeek: number) => {
        const formatter = new Intl.DateTimeFormat(locale, {
            weekday: 'narrow',
        });
        const days = [];
        const startIndex = firstDayOfWeek;
        for (let i = 0; i < 7; i++) {
            const dayIndex = (startIndex + i) % 7;
            const date = new Date(Date.UTC(2021, 0, dayIndex + 3));
            days.push(formatter.format(date));
        }
        return days;
    };

    const getFirstDayOfMonth = () => {
        const firstDay = new Date(year, month, 1).getDay();
        const offset = (firstDay - firstDayOfWeek + 7) % 7;
        return offset;
    };

    const getLastDateOfMonth = () => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getLastDateOfPreviousMonth = () => {
        return new Date(year, month, 0).getDate();
    };

    const getLastDayOfMonth = (lastDateOfMonth: number) => {
        const dayEnd = new Date(year, month, lastDateOfMonth).getDay();
        const offset = (dayEnd - firstDayOfWeek + 7) % 7;
        return offset;
    };

    const generatePreviousMonthDaysHTML = (
        firstDayOfMonth: number,
        lastDateOfPreviousMonth: number,
    ) => {
        const html = [];
        for (let i = firstDayOfMonth; i > 0; i--) {
            html.push(
                <li
                    key={`prev-${i}`}
                    className="invisible pb-2 text-[12px] leading-6 text-text-placeholder"
                >
                    {lastDateOfPreviousMonth - i + 1}
                </li>,
            );
        }
        return html;
    };

    const generateCurrentMonthDaysHTML = (lastDateOfMonth: number) => {
        const html = [];
        for (let i = 1; i <= lastDateOfMonth; i++) {
            const date = new Date(year, month, i);
            const dateString = formatDateToString(date);
            let content: ReactElement = <span>{i}</span>;

            const isToday =
                i === new Date().getDate() &&
                month === new Date().getMonth() &&
                year === new Date().getFullYear()
                    ? 'font-bold underline' // today
                    : '';

            const isSelectable =
                isDateInRange(date) &&
                parsedSelectableDates.includes(dateString)
                    ? 'text-dark-red' // selectable
                    : '';

            const isSelected =
                currentDate && dateString === currentDate
                    ? 'text-falck-red bg-falck-red/10'
                    : ''; // selected

            if (isSelectable) {
                content = (
                    <a
                        href=""
                        className=""
                        onClick={(e) => handleDateClick(e, date)}
                    >
                        {i}
                    </a>
                );
            }

            html.push(
                <li
                    key={`current-${i}`}
                    className={`pb-2 text-[12px] leading-6 ${isToday} ${isSelectable} ${isSelected}`}
                >
                    {content}
                </li>,
            );
        }
        return html;
    };

    const generateNextMonthDaysHTML = (dayEnd: number) => {
        const html = [];
        for (let i = dayEnd; i < 6; i++) {
            html.push(
                <li
                    key={`next-${i}`}
                    className="invisible pb-2 text-[12px] leading-6 text-text-placeholder" // inactive
                >
                    {i - dayEnd + 1}
                </li>,
            );
        }
        return html;
    };

    const formatDateToString = (date: Date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isDateInRange = (date: Date) => {
        const dateComp = date.getTime();
        const minDateComp = minAllowedDate
            ? new Date(minAllowedDate).getTime()
            : null;
        const maxDateComp = maxAllowedDate
            ? new Date(maxAllowedDate).getTime()
            : null;

        return (
            (minDateComp === null || dateComp >= minDateComp) &&
            (maxDateComp === null || dateComp <= maxDateComp)
        );
    };

    function handleDateClick(
        e: React.MouseEvent<HTMLAnchorElement>,
        date: Date,
    ) {
        e.preventDefault();
        const formattedDateString = formatDateToString(date);
        if (
            isDateInRange(date) &&
            parsedSelectableDates.includes(formattedDateString)
        ) {
            setCurrentDate(formattedDateString);

            onDateSelected?.(formattedDateString);
        }
    }

    function handlePrevMonth(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        const newDate = new Date(date);
        newDate.setMonth(date.getMonth() - 1);
        setDate(newDate);
    }

    function handleNextMonth(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        const newDate = new Date(date);
        newDate.setMonth(date.getMonth() + 1);
        setDate(newDate);
    }

    const renderCalendar = () => {
        const firstDayOfMonth = getFirstDayOfMonth();
        const lastDateOfMonth = getLastDateOfMonth();
        const lastDateOfPreviousMonth = getLastDateOfPreviousMonth();
        const dayEnd = getLastDayOfMonth(lastDateOfMonth);

        return (
            <div className="min-w-fit max-w-xs bg-white">
                <header className="mb-10 flex items-center justify-between">
                    <button
                        id="date-picker-prev"
                        className="h-[16px] w-[16px] disabled:text-text-placeholder"
                        onClick={handlePrevMonth}
                        type="button"
                    >
                        <svg
                            fill="none"
                            height="16"
                            viewBox="0 0 16 16"
                            width="16"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="m7.99953 12.9991-4.99953-4.99957 4.99953-4.99953.87754.87754-3.48021 3.50828h7.60314v1.22743h-7.60127l3.47928 3.50825z"
                                fill="currentColor"
                            />
                        </svg>
                    </button>
                    <span className="text-sm font-bold">{`${monthNames[month]} ${year}`}</span>
                    <button
                        id="date-picker-next"
                        className="h-[16px] w-[16px] disabled:text-text-placeholder"
                        onClick={handleNextMonth}
                        type="button"
                    >
                        <svg
                            fill="none"
                            height="16"
                            viewBox="0 0 17 16"
                            width="17"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="m8.50047 3 4.99953 4.99953-4.99953 4.99957-.87754-.8776 3.48017-3.50825h-7.6031v-1.22743h7.6013l-3.47931-3.50828z"
                                fill="currentColor"
                            />
                        </svg>
                    </button>
                </header>
                <main>
                    <ul className="grid grid-cols-7 gap-x-2 text-center">
                        {dayNames.map((name, index) => (
                            <li
                                className="pb-2 text-[12px] leading-6"
                                key={`weekday-${index}`}
                            >
                                {name}
                            </li>
                        ))}
                    </ul>
                    <ul className="grid grid-cols-7 gap-x-2 text-center text-text-placeholder">
                        {generatePreviousMonthDaysHTML(
                            firstDayOfMonth,
                            lastDateOfPreviousMonth,
                        )}
                        {generateCurrentMonthDaysHTML(lastDateOfMonth)}
                        {generateNextMonthDaysHTML(dayEnd)}
                    </ul>
                </main>
            </div>
        );
    };

    return <div>{renderCalendar()}</div>;
}
